import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  useToast,
  Text,
  Link,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { translateAuthError } from '../utils/errorTranslator';
import { PasswordHelpTooltip } from '../components/common/HelpTooltipCard';

interface LocationState {
  from: {
    pathname: string;
  };
}

const RegisterPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const from = (location.state as LocationState)?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const nameTrim = name.trim();
    const emailTrim = email.trim().toLowerCase();
    if (!nameTrim || !nameTrim.includes(' ')) {
      toast({ title: 'Registrace selhala', description: 'Zadejte prosím jméno i příjmení.', status: 'error', duration: 5000, isClosable: true });
      setIsLoading(false);
      return;
    }
    if (!emailTrim || !emailTrim.includes('@')) {
      toast({ title: 'Registrace selhala', description: 'Zadejte platný e‑mail.', status: 'error', duration: 5000, isClosable: true });
      setIsLoading(false);
      return;
    }
    if (!password || password.length < 8) {
      toast({ title: 'Registrace selhala', description: 'Heslo musí mít alespoň 8 znaků.', status: 'error', duration: 5000, isClosable: true });
      setIsLoading(false);
      return;
    }

    try {
      // Backend Register accepts name or first/last
      const response = await api.post('/auth/register', {
        email: emailTrim,
        password,
        name,
      });
      const { token, user } = response.data;
      await login(token, user, true);
      toast({ title: 'Účet vytvořen', status: 'success', duration: 3000 });
      navigate(from, { replace: true });
    } catch (error: any) {
      toast({
        title: 'Registrace selhala',
        description: translateAuthError(error, 'register'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
      <Box w="100%" maxW="md" p={8} borderWidth={1} borderRadius={8} boxShadow="lg">
        <VStack as="form" onSubmit={handleSubmit} spacing={4} align="stretch">
          <Heading as="h2" size="lg" textAlign="center" mb={2}>
            Vytvořit účet
          </Heading>

          <FormControl id="name" isRequired>
            <FormLabel>Jméno a příjmení</FormLabel>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="např. Jan Novák"
            />
          </FormControl>

          <FormControl id="email" isRequired>
            <FormLabel>E‑mail</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="např. jan@klub.cz"
            />
          </FormControl>

          <FormControl id="password" isRequired>
            <FormLabel display="flex" alignItems="center" justifyContent="space-between">
              <Box>Heslo</Box>
              <PasswordHelpTooltip />
            </FormLabel>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Zadejte heslo (min. 8 znaků)"
            />
          </FormControl>

          <Button type="submit" colorScheme="blue" width="full" mt={2} isLoading={isLoading}>
            Zaregistrovat se
          </Button>

          <Text fontSize="sm" textAlign="center">
            Už máte účet?{' '}
            <Link color="blue.500" href="/login">
              Přihlaste se
            </Link>
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default RegisterPage;
